/**
 * Build mode detection.
 *
 * VITE_DESKTOP is set to "true" during desktop (Electron) builds.
 * This gates features that should only appear in the web version.
 *
 * VITE_DISABLE_ATLAS is set to "true" for non-Atlas deployments
 * (the "core" SKU without paid Atlas features). When disabled, ALL
 * Atlas, Search Canvas, and Atlas-aware Minimap features become
 * unreachable regardless of plan tier — useful for white-label
 * deploys, self-hosted SKUs without an atlas store, and OSS forks.
 *
 * Tier gating still applies independently inside an Atlas-enabled
 * build: a Free user in the Atlas SKU still can't reach Pro+ features.
 * The flag is a *build-time* lever; tier checks remain *runtime*.
 *
 * See documents/v1/SYSTEM_STATUS.md for the isolation policy.
 */

export const IS_DESKTOP = import.meta.env.VITE_DESKTOP === 'true';
export const IS_WEB = !IS_DESKTOP;
export const IS_ATLAS_DISABLED = import.meta.env.VITE_DISABLE_ATLAS === 'true';
export const IS_ATLAS_BUILD = !IS_ATLAS_DISABLED;

/**
 * When true, GitHub Contents-API calls are routed through the
 * `github-proxy` Supabase edge function (Ship 7B). The proxy holds a
 * shared GitHub App token so users don't have to supply their own
 * PAT to hit the 5,000/h limit. Per-user quota inside the proxy
 * prevents one caller from draining the shared pool.
 *
 * Defaults to OFF — operators opt in by setting the env flag at
 * build/deploy time. When OFF, the client either uses the user's
 * own PAT (Ship 7A) or falls back to unauthenticated 60/h calls.
 */
export const IS_GITHUB_PROXY_ENABLED =
  import.meta.env.VITE_USE_GITHUB_PROXY === 'true';

/**
 * Process Engine SKU. When VITE_DISABLE_PROCESS_ENGINE=true, the
 * `/processengine` route and all related UI are unreachable, the
 * runner is tree-shaken out, and any adapter modules fail-closed.
 *
 * Defense-in-depth mirrors the Atlas pattern: build-time flag,
 * route-time tier gate, component-time prop dropping. Process Engine
 * consumes Flow-weaver shapes + Atlas service catalogues; it never
 * writes back to either system.
 */
export const IS_PROCESS_ENGINE_DISABLED =
  import.meta.env.VITE_DISABLE_PROCESS_ENGINE === 'true';
export const IS_PROCESS_ENGINE_BUILD = !IS_PROCESS_ENGINE_DISABLED;

/**
 * Final (executive presentation) SKU. Read-only aggregator across
 * Flow-weaver + Atlas + Process Engine — never executes or edits.
 * When VITE_DISABLE_FINAL=true the `/final` route is unreachable
 * and the bundle composer is stripped.
 */
export const IS_FINAL_DISABLED =
  import.meta.env.VITE_DISABLE_FINAL === 'true';
export const IS_FINAL_BUILD = !IS_FINAL_DISABLED;
